import React from 'react';
import { MapPin, Clock, Phone, Mail, CheckCircle2, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHelmet from '../../components/SEOHelmet';
import { companyInfo } from '../../mockData';

const RanchoMiragePage = () => {
  return (
    <div style={{ backgroundColor: 'var(--bg-page)', minHeight: '100vh' }}>
      <SEOHelmet 
        title="Process Server Rancho Mirage CA | Legal Document Services | Legal Document Specialist"
        description="Rancho Mirage VIP process server for the Playground of Presidents. Exclusive service for Eisenhower Medical Center, luxury estates, and celebrity residential areas. White-glove confidential document handling."
        keywords="process server Rancho Mirage CA, legal document services Rancho Mirage, court filing Rancho Mirage, skip tracing Rancho Mirage, Riverside County process server"
        canonical="https://legaldocspecialist.com/cities/rancho-mirage"
      />
      <Header />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              Process Server Rancho Mirage, California
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Elite legal document services in Rancho Mirage, CA. Professional, registered and bonded process servers providing discreet service to luxury resorts and the presidential retreat community.
            </p>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Now
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services & Presidential Community Focus */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Rancho Mirage Legal Document Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Exclusive legal support services for Rancho Mirage's luxury resort community and prestigious residential areas.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {/* Services */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <CheckCircle2 size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Elite Services
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  'Process Serving & Legal Notices',
                  'Court Document Filing',
                  'County Recording Services',
                  'Document Retrieval & Research',
                  'Skip Tracing & Locate Services'
                ].map((service, index) => (
                  <li key={service} style={{ 
                    padding: '0.75rem 0',
                    borderBottom: index < 4 ? '1px solid var(--border-light)' : 'none',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <CheckCircle2 size={16} color="var(--brand-primary)" style={{ marginRight: '0.75rem' }} />
                    <span style={{ color: 'var(--text-primary)' }}>{service}</span>
                  </li>
                ))}
              </ul>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '1rem' }}>
                Ultra-discreet service for high-profile clients and luxury resort properties.
              </p>
            </div>

            {/* Rancho Mirage Prestige */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <Crown size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Prestige Community Expertise
              </h3>
              <div style={{ color: 'var(--text-primary)', lineHeight: '1.6' }}>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Presidential Retreat Experience</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Familiar with high-security and exclusive areas</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Luxury Resort Expertise</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Professional service for world-class hospitality venues</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Celebrity & VIP Discretion</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Ultra-confidential service for high-profile clients</span>
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  The highest standards of professionalism and discretion for Rancho Mirage's elite community.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exclusive Service Standards */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Why Choose Us for Rancho Mirage?
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              Elite service standards meet local expertise. We understand Rancho Mirage's exclusive community and the discretion required for high-profile legal matters.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
            {[
              {
                icon: <Crown size={32} />,
                title: 'Elite Community Experience',
                description: 'Specialized experience serving celebrity, political, and business elite communities with absolute discretion.'
              },
              {
                icon: <Clock size={32} />,
                title: 'VIP Service Options',
                description: 'All service levels with enhanced privacy protocols for high-profile and time-sensitive matters.'
              },
              {
                icon: <CheckCircle2 size={32} />,
                title: 'Maximum Security Standards',
                description: 'Registered, bonded, and background-checked with the highest security clearance and confidentiality protocols.'
              }
            ].map((feature) => (
              <div key={feature.title} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{ color: 'var(--brand-primary)', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                  {feature.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{feature.title}</h3>
                <p className="body-text" style={{ color: 'var(--text-secondary)' }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area Coverage */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Rancho Mirage Service Coverage
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Serving Rancho Mirage and surrounding prestigious Coachella Valley communities with elite-level legal document services.
            </p>
          </div>

          <div style={{
            backgroundColor: 'var(--bg-page)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
              Elite Communities Served from Rancho Mirage
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {[
                'Rancho Mirage', 'Palm Desert', 'La Quinta', 'Indian Wells',
                'Palm Springs', 'Cathedral City', 'Bermuda Dunes', 'Thousand Palms',
                'Mission Hills', 'The Lakes', 'Thunderbird Heights', 'Magnesia Falls'
              ].map((city, index) => (
              <div key={city} style={{
                  padding: '0.75rem',
                  backgroundColor: index === 0 ? 'var(--brand-primary)' : 'var(--bg-card)',
                  borderRadius: '6px',
                  border: '1px solid var(--border-light)'
                }}>
                  <span style={{ 
                    color: index === 0 ? 'white' : 'var(--text-primary)',
                    fontWeight: index === 0 ? '600' : 'normal'
                  }}>{city}</span>
                </div>
              ))}
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Comprehensive coverage of central Coachella Valley's most exclusive communities and resort destinations.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Contact Us for Rancho Mirage Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Ready to get started? Contact Legal Document Specialist for elite, confidential service in Rancho Mirage.
            </p>
          </div>

          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            backgroundColor: 'var(--bg-card)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '2rem' }}>
              <Phone size={24} color="var(--brand-primary)" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '1.2rem' }}>{companyInfo.phone}</div>
              <div style={{ color: 'var(--text-secondary)' }}>Monday-Friday 9AM-5PM PST • VIP support available</div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <Mail size={24} color="var(--brand-primary)" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{companyInfo.email}</div>
              <div style={{ color: 'var(--text-secondary)' }}>Encrypted communication • Absolute confidentiality</div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '2rem' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Online
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Request Quote
              </Link>
            </div>

            <div style={{ 
              backgroundColor: 'var(--bg-page)', 
              padding: '1rem', 
              borderRadius: '6px',
              border: '1px solid var(--border-light)'
            }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                <strong>Serving the Presidential Retreat Community</strong><br />
                Elite Standards • Ultra-Discreet • Celebrity & VIP Experience
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RanchoMiragePage;